import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router';
import type { SupportRequestRow } from '@pqrs/contracts';
import { Button } from '@xiltrion/ui/button';
import { Icono } from '@xiltrion/ui/icono';
import { Input } from '@xiltrion/ui/input';
import { Filters, createFilterQuery, createFilterRule, flattenFilterRules, type FilterField, type FilterOperator, type FilterQuery } from '@xiltrion/ui';
import { Tabla, type ColumnaTabla } from '@xiltrion/ui/bloques/tabla';
import { EmptyState, ErrorState, StatusTag, fmtDate } from '@/components/ui';
import { useSupportRequests } from './api';
import { SupportRequestItem, SupportStatusBadge } from './SupportPanel';

export type SupportBox = 'inbox' | 'sent';
/** Estado de las solicitudes. Sin `estado` en la URL se ven las pendientes; `all`, todas. */
type Status = 'PENDING' | 'ANSWERED' | 'all';
const STATUS_LABEL: Record<Exclude<Status, 'all'>, string> = { PENDING: 'Pendientes', ANSWERED: 'Respondidas' };

/**
 * Lista de apoyos entre áreas: hoy la pestaña «Apoyos enviados» de «Mis tareas» (`sent`; los por responder salen como
 * tareas, `SupportTaskRow`). Cada fila abre el expediente en su pestaña de apoyos; al desplegarla se lee (y, en
 * `inbox`, se responde) sin salir de la lista. Misma estructura que el Archivo: cuadro de búsqueda y barra de filtros
 * en un contenedor, y la tabla debajo.
 */
export function SupportRequestsView({ box }: { box: SupportBox }) {
  const nav = useNavigate();
  const [sp, setSp] = useSearchParams();
  const status: Status = sp.get('estado') === 'ANSWERED' ? 'ANSWERED' : sp.get('estado') === 'all' ? 'all' : 'PENDING';
  const q = sp.get('q') ?? '';
  const [text, setText] = useState(q);
  const query = useSupportRequests({ box, ...(status !== 'all' ? { status } : {}) });

  const set = (patch: Record<string, string>) => {
    const next = new URLSearchParams(sp);
    for (const [k, v] of Object.entries(patch)) { if (v) next.set(k, v); else next.delete(k); }
    setSp(next, { replace: true });
  };
  useEffect(() => { setText(q); }, [q]);
  useEffect(() => {
    const t = window.setTimeout(() => { if (text.trim() !== q) set({ q: text.trim() }); }, 350);
    return () => window.clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps

  // La lista es corta y viene completa: el texto se busca en memoria sobre lo que se ve en la fila.
  const rows = useMemo(() => {
    const all = query.data ?? [];
    const needle = q.trim().toLocaleLowerCase('es');
    if (!needle) return all;
    return all.filter((r) => [r.case.radication, r.case.subject, r.case.caseTypeName, r.targetUnit.name, r.requestedBy.name, r.answeredBy?.name]
      .some((s) => s?.toLocaleLowerCase('es').includes(needle)));
  }, [query.data, q]);

  const columnas: ColumnaTabla<SupportRequestRow>[] = [
    { key: 'radication', titulo: 'Radicado', valor: (r) => r.case.radication ?? '', render: (r) => <span className="tnum">{r.case.radication ?? 'Sin radicado'}</span>, secundario: (r) => r.case.caseTypeName ?? '', unaLinea: true, ancho: '9.5rem' },
    { key: 'subject', titulo: 'Asunto', valor: (r) => r.case.subject, lineas: 2, secundario: (r) => <StatusTag status={r.case.status} /> },
    box === 'inbox'
      ? { key: 'unit', titulo: 'Para', valor: (r) => r.targetUnit.name, secundario: (r) => `De ${r.requestedBy.name}` }
      : { key: 'unit', titulo: 'Área', valor: (r) => r.targetUnit.name, secundario: (r) => (r.answeredBy ? `Respondió ${r.answeredBy.name}` : '') },
    { key: 'createdAt', titulo: 'Pedida', valor: (r) => r.createdAt, render: (r) => fmtDate(r.createdAt), secundario: (r) => (r.answeredAt ? `Respondida ${fmtDate(r.answeredAt)}` : ''), unaLinea: true },
    { key: 'status', titulo: 'Estado', valor: (r) => r.status, render: (r) => <SupportStatusBadge status={r.status} />, unaLinea: true },
    { key: 'open', titulo: '', valor: () => '', ordenable: false, ancho: '3rem', render: (r) => <Button variant="ghost" size="icon-sm" aria-label={`Abrir el caso ${r.case.radication ?? ''}`} onClick={(e) => { e.stopPropagation(); nav(`/pqrs/${r.caseId}?tab=apoyos`); }}><Icono nombre="mdi:open-in-new" /></Button> },
  ];
  return (
    <div>
      <div className="bg-card border border-border rounded-lg p-3 mb-4 flex flex-col gap-3">
        <div className="relative">
          <Icono nombre='mdi:magnify' className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" aria-hidden />
          <Input value={text} onChange={(e) => setText(e.target.value)} placeholder="Radicado, asunto, área o persona…" className="pl-9 h-10" aria-label="Buscar en los apoyos" />
        </div>
        <SupportFilterBar status={status} onChange={(s) => set({ estado: s === 'PENDING' ? '' : s })} />
      </div>

      {query.isError ? <ErrorState error={query.error} onRetry={() => void query.refetch()} title="No pudimos cargar las solicitudes de apoyo." /> : (
        <Tabla<SupportRequestRow>
          columnas={columnas}
          filas={rows}
          claveFila={(r) => r.id}
          onClicFila={(r) => nav(`/pqrs/${r.caseId}?tab=apoyos`)}
          cargando={query.isLoading}
          densa
          expandir={(r) => <ul className="list-none m-0 p-0"><SupportRequestItem r={r} caseId={r.caseId} defaultOpen /></ul>}
          vacio={q ? <EmptyState icon="mdi:account-multiple-outline" title={`Sin resultados para «${q}»`} hint="Prueba con el radicado completo, el asunto o el nombre del área." />
            : <EmptyState icon="mdi:account-multiple-outline"
              title={box === 'inbox' ? (status === 'PENDING' ? 'Tus áreas no tienen solicitudes de apoyo pendientes.' : 'No hay solicitudes de apoyo para tus áreas.') : 'No has pedido apoyo con este filtro.'}
              hint={box === 'inbox' ? 'Aquí llegan las solicitudes a las áreas donde respondes o que lideras.' : 'Pide apoyo desde la pestaña «Apoyos» de un caso que estés trabajando o desde la tarea «Responder».'} />}
        />
      )}
    </div>
  );
}

const IS: FilterOperator[] = [{ value: 'is', label: 'es', arity: 'one' }];
const first = (v: unknown): string | undefined => (Array.isArray(v) ? (v[0] as string | undefined) : typeof v === 'string' ? v : undefined);

const statusToQuery = (s: Status): FilterQuery => createFilterQuery(s === 'all' ? [] : [createFilterRule({ id: 'r-estado', path: ['estado'], operator: 'is', value: [s] })]);
function queryToStatus(q: FilterQuery): Status {
  for (const r of flattenFilterRules(q)) {
    if (r.path[0] !== 'estado' || !r.operator) continue;
    const v = first(r.value);
    if (v === 'PENDING' || v === 'ANSWERED') return v;
  }
  return 'all';
}

/** Estado como chip de filtro, igual que en el Archivo: sin chip se ven todas; la URL guarda solo lo que filtra. */
function SupportFilterBar({ status, onChange }: { status: Status; onChange: (s: Status) => void }) {
  const [query, setQuery] = useState<FilterQuery>(() => statusToQuery(status));
  useEffect(() => { setQuery((cur) => (queryToStatus(cur) === status ? cur : statusToQuery(status))); }, [status]);
  const fields = useMemo<FilterField[]>(() => [
    { id: 'estado', label: 'Estado', type: 'select', operators: IS, searchable: false, icon: <Icono nombre="mdi:record-circle-outline" />, options: Object.entries(STATUS_LABEL).map(([value, label]) => ({ value, label })) },
  ], []);
  return (
    <Filters
      fields={fields}
      query={query}
      showClear
      size="sm"
      onQueryChange={(q) => {
        setQuery(q);
        const next = queryToStatus(q);
        if (next !== status) onChange(next);
      }}
    />
  );
}
